import { useEffect, useRef } from "react";
import { environmentMarkLabel, PRODUCTION_APP_URL } from "@/lib/environment";
import { applyEnvironmentDocumentMarks } from "@/lib/environmentDocumentMarks";
import { ENV_BANNER_HEIGHT_VAR } from "@/lib/environmentMarkStyles";

/**
 * Permanent strip at the very top of every page outside production (#1099),
 * so DEV can't be mistaken for PROD. Mounted once at the app root, above the
 * routes: it sits in the normal flow and pushes the page down instead of
 * covering it, and it can't be dismissed.
 *
 * It also owns the browser-tab marks ("[DEV] " title prefix, orange favicon)
 * and publishes its height as `--env-banner-height` for the few fixed
 * elements (mobile menu toggle, toasts) that must stay clear of it.
 *
 * Renders nothing and touches nothing in production.
 */
export const EnvironmentBanner = () => {
  const label = environmentMarkLabel();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!label) {
      return undefined;
    }
    return applyEnvironmentDocumentMarks(document, label);
  }, [label]);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) {
      return undefined;
    }
    const root = document.documentElement;
    const publishHeight = () =>
      root.style.setProperty(ENV_BANNER_HEIGHT_VAR, `${banner.offsetHeight}px`);
    publishHeight();
    // The text wraps on narrow screens, so the height follows the viewport.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publishHeight);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(ENV_BANNER_HEIGHT_VAR);
    };
  }, [label]);

  if (!label) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label={`${label} environment notice`}
      className="w-full border-b-2 border-amber-600 bg-env-stripes px-2 py-1.5 text-center"
    >
      <p className="inline-block rounded bg-amber-100 px-3 py-0.5 text-sm font-medium text-amber-950 shadow-sm">
        <strong className="font-extrabold tracking-wide">{label}</strong>, test environment. Data is
        reset on every deploy. Real consultants belong in PROD <span aria-hidden="true">→</span>{" "}
        {/* 24px tap target (#1038) without moving the text. */}
        <a
          href={PRODUCTION_APP_URL}
          className="-mx-1 -my-1 inline-block px-1 py-1 font-semibold underline underline-offset-2 hover:text-amber-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-950"
        >
          app.bondit.dk
        </a>
      </p>
    </div>
  );
};
